import { useEffect, useState } from 'react';
import { Alert, Box, Card, CardContent, Grid, MenuItem, TextField, Typography } from '@mui/material';
import { taxBooksService } from '../../../services/api';
import TaxBookPage from './TaxBookPage';

const money = (value) => `$${(parseFloat(value || 0) || 0).toFixed(2)}`;

const annex1Columns = [
  { key: 'fecha_emision_documento', label: 'Fecha' },
  { key: 'tipo_documento', label: 'Tipo DTE' },
  { key: 'nit_o_nrc', label: 'NIT/NRC' },
  { key: 'nombre_razon_social_o_denominacion', label: 'Cliente' },
  { key: 'ventas_gravadas_locales', label: 'Gravadas' },
  { key: 'debito_fiscal', label: 'Débito fiscal' },
  { key: 'total_ventas', label: 'Total' },
];

const annex2Columns = [
  { key: 'fecha_emision_documento', label: 'Fecha' },
  { key: 'tipo_documento', label: 'Tipo DTE' },
  { key: 'numero_documento_del', label: 'No. doc. (del)' },
  { key: 'numero_documento_al', label: 'No. doc. (al)' },
  { key: 'ventas_gravadas_locales', label: 'Gravadas (IVA incl.)' },
  { key: 'total_ventas', label: 'Total ventas' },
];

const annex3Columns = [
  { key: 'fecha_emision_documento', label: 'Fecha' },
  { key: 'tipo_documento', label: 'Tipo' },
  { key: 'numero_documento', label: 'Número' },
  { key: 'nit_proveedor', label: 'NIT' },
  { key: 'nombre_proveedor', label: 'Proveedor' },
  { key: 'total_compras', label: 'Total compras' },
  { key: 'credito_fiscal', label: 'Crédito fiscal' },
];

const annex10Columns = [
  { key: 'nit_sujeto', label: 'NIT sujeto' },
  { key: 'fecha_emision', label: 'Fecha' },
  { key: 'tipo_documento', label: 'Tipo DTE' },
  { key: 'monto_sujeto', label: 'Monto sujeto' },
  { key: 'monto_retencion', label: 'Retención' },
];

const F07AnnexesPage = () => {
  const now = new Date();
  const [filters, setFilters] = useState({
    month: String(now.getMonth() + 1),
    year: String(now.getFullYear()),
    branchId: '',
  });
  const [branches, setBranches] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadBranches = async () => {
      try {
        const branchRows = await taxBooksService.getCatalog('branches');
        setBranches(branchRows || []);
      } catch {
        setBranches([]);
      }
    };

    loadBranches();
  }, []);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          Anexos F07
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Generación por anexo (formato MH) para el período seleccionado.
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                label="Mes"
                value={filters.month}
                onChange={(event) => setFilters((prev) => ({ ...prev, month: event.target.value }))}
              >
                {Array.from({ length: 12 }, (_, index) => (
                  <MenuItem key={index + 1} value={String(index + 1)}>
                    {String(index + 1).padStart(2, '0')}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Año"
                value={filters.year}
                onChange={(event) => setFilters((prev) => ({ ...prev, year: event.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Sucursal"
                value={filters.branchId || ''}
                onChange={(event) => setFilters((prev) => ({ ...prev, branchId: event.target.value }))}
              >
                <MenuItem value="">Todas</MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.branch_name} ({branch.codigo_establecimiento})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TaxBookPage
        title="Anexo 1 - Ventas a Contribuyentes"
        subtitle="CCF (03)."
        previewPath="annexes/f07/annex-1/preview"
        exportPath="annexes/f07/annex-1/export"
        columns={annex1Columns}
        summaryCards={[
          { key: 'count', label: 'Documentos' },
          { key: 'totalVentas', label: 'Total ventas', format: money },
          { key: 'totalDebitoFiscal', label: 'Débito fiscal', format: money },
        ]}
        sharedFilters={filters}
        hideFilterCard
        hideRefreshButton
        downloadLabel="Generar formato MH"
      />

      <Box sx={{ mt: 4 }}>
        <TaxBookPage
          title="Anexo 2 - Ventas a Consumidor Final"
          subtitle="Agrupado por fecha y tipo."
          previewPath="books/sales-consumer-final/preview"
          exportPath="books/sales-consumer-final/export"
          columns={annex2Columns}
          summaryCards={[
            { key: 'count', label: 'Filas' },
            { key: 'totalVentas', label: 'Total ventas', format: money },
          ]}
          sharedFilters={filters}
          hideFilterCard
          hideRefreshButton
          downloadLabel="Generar formato MH"
        />
      </Box>

      <Box sx={{ mt: 4 }}>
        <TaxBookPage
          title="Anexo 3 - Detalle de Compras"
          subtitle="Compras a contribuyentes (detalle)."
          previewPath="annexes/f07/annex-3/preview"
          exportPath="annexes/f07/annex-3/export"
          columns={annex3Columns}
          summaryCards={[
            { key: 'count', label: 'Documentos' },
            { key: 'totalCompras', label: 'Total compras', format: money },
            { key: 'totalCreditoFiscal', label: 'Crédito fiscal', format: money },
          ]}
          sharedFilters={filters}
          hideFilterCard
          hideRefreshButton
          downloadLabel="Generar formato MH"
        />
      </Box>

      <Box sx={{ mt: 4 }}>
        <TaxBookPage
          title="Anexo 10 - Retenciones y Percepciones (Casilla 170)"
          subtitle="Basado en DTE 07 con sello y sin anulación."
          previewPath="annexes/f07/annex-10/preview"
          exportPath="annexes/f07/annex-10/export"
          columns={annex10Columns}
          summaryCards={[
            { key: 'count', label: 'Filas' },
            { key: 'totalSujeto', label: 'Monto sujeto', format: money },
            { key: 'totalRetenido', label: 'Total retenido', format: money },
          ]}
          sharedFilters={filters}
          hideFilterCard
          hideRefreshButton
          downloadLabel="Generar formato MH"
        />
      </Box>

      <Box sx={{ mt: 4 }}>
        <TaxBookPage
          title="Anexo 12 - Notas de Crédito"
          subtitle="DTE 05."
          previewPath="annexes/f07/annex-12/preview"
          exportPath="annexes/f07/annex-12/export"
          columns={annex1Columns}
          summaryCards={[
            { key: 'count', label: 'Documentos' },
            { key: 'totalVentas', label: 'Total', format: money },
            { key: 'totalDebitoFiscal', label: 'IVA', format: money },
          ]}
          sharedFilters={filters}
          hideFilterCard
          hideRefreshButton
          downloadLabel="Generar formato MH"
        />
      </Box>

      <Box sx={{ mt: 4 }}>
        <TaxBookPage
          title="Anexo 13 - Notas de Débito"
          subtitle="DTE 06."
          previewPath="annexes/f07/annex-13/preview"
          exportPath="annexes/f07/annex-13/export"
          columns={annex1Columns}
          summaryCards={[
            { key: 'count', label: 'Documentos' },
            { key: 'totalVentas', label: 'Total', format: money },
            { key: 'totalDebitoFiscal', label: 'IVA', format: money },
          ]}
          sharedFilters={filters}
          hideFilterCard
          hideRefreshButton
          downloadLabel="Generar formato MH"
        />
      </Box>
    </Box>
  );
};

export default F07AnnexesPage;

